// lib/footfall/layout.ts
// A scenario may reshape the centre before it re-lets it: a unit cut into smaller ones, or neighbours joined into one.
// The reshaped units replace their parents in the venue, so every map, card, price and agent read sees one plan.
import type { PropertyVenue, PulseLayoutChange, PulseVenueFeature } from '#types/types';

type Pt = [number, number];
export const LAYOUT_MAX = 20;
export const SPLIT_PARTS = { min: 2, max: 4 } as const;
const SHARE_MIN = 0.1;
/** Two units this close (m) share a wall: the plans' outlines are drawn with gaps for the walls themselves. */
const ADJACENT_M = 3;

/** Metres from a reference point, flat: a shopping centre is small enough for it. */
const projector = (origin: Pt) => {
  const k = Math.cos(origin[1] * Math.PI / 180) * 111_320;
  return {
    to: ([x, y]: Pt): Pt => [(x - origin[0]) * k, (y - origin[1]) * 110_540],
    from: ([x, y]: Pt): Pt => [origin[0] + x / k, origin[1] + y / 110_540],
  };
};
const area = (ring: Pt[]) => Math.abs(ring.reduce((s, [x, y], i) => { const [x2, y2] = ring[(i + 1) % ring.length]!; return s + x * y2 - x2 * y; }, 0)) / 2;
const largest = (rings: Pt[][]) => rings.reduce((a, b) => b.length > a.length ? b : a);

/** Keeps the part of a ring where p·u ≤ c (Sutherland–Hodgman against one half-plane). */
function clip(ring: Pt[], u: Pt, c: number, keepBelow: boolean): Pt[] {
  const inside = (p: Pt) => keepBelow ? p[0] * u[0] + p[1] * u[1] <= c : p[0] * u[0] + p[1] * u[1] >= c;
  const out: Pt[] = [];
  ring.forEach((p, i) => {
    const q = ring[(i + 1) % ring.length]!;
    const pin = inside(p), qin = inside(q);
    if (pin) out.push(p);
    if (pin !== qin) {
      const dp = p[0] * u[0] + p[1] * u[1], dq = q[0] * u[0] + q[1] * u[1];
      const t = (c - dp) / (dq - dp);
      out.push([p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])]);
    }
  });
  return out;
}

/** The ring's long axis: the direction its points spread along most. */
function longAxis(ring: Pt[]): Pt {
  const n = ring.length, mx = ring.reduce((s, p) => s + p[0], 0) / n, my = ring.reduce((s, p) => s + p[1], 0) / n;
  let xx = 0, yy = 0, xy = 0;
  for (const [x, y] of ring) { xx += (x - mx) ** 2; yy += (y - my) ** 2; xy += (x - mx) * (y - my); }
  const angle = Math.atan2(2 * xy, xx - yy) / 2;
  return [Math.cos(angle), Math.sin(angle)];
}

/** Cuts a ring across its long axis into parts holding the given shares of its area, in order along the axis. */
export function splitRing(ring: Pt[], shares: number[]): Pt[][] {
  const u = longAxis(ring);
  const total = area(ring);
  const along = ring.map(p => p[0] * u[0] + p[1] * u[1]);
  const lo = Math.min(...along), hi = Math.max(...along);
  const cuts: number[] = [];
  let cumulative = 0;
  for (const share of shares.slice(0, -1)) {
    cumulative += share;
    let a = lo, b = hi;
    for (let i = 0; i < 50; i++) { const mid = (a + b) / 2; if (area(clip(ring, u, mid, true)) < cumulative * total) a = mid; else b = mid; }
    cuts.push((a + b) / 2);
  }
  return shares.map((_, i) => {
    let part = ring;
    if (i > 0) part = clip(part, u, cuts[i - 1]!, false);
    if (i < cuts.length) part = clip(part, u, cuts[i]!, true);
    return part;
  });
}

function segmentDistance(p: Pt, a: Pt, b: Pt) {
  const dx = b[0] - a[0], dy = b[1] - a[1], len = dx * dx + dy * dy;
  const t = len ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len)) : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
}
const ringsDistance = (a: Pt[][], b: Pt[][]) => {
  let best = Infinity;
  for (const [from, to] of [[a, b], [b, a]] as const) for (const r of from) for (const p of r) for (const s of to) s.forEach((q, i) => { best = Math.min(best, segmentDistance(p, q, s[(i + 1) % s.length]!)); });
  return best;
};

const derivedId = (change: PulseLayoutChange, part = 0) => change.kind === 'split' ? `${change.shopId}-${part + 1}` : change.shopIds.join('+');
/** The units a change takes away. */
export const parentsOf = (change: PulseLayoutChange) => change.kind === 'split' ? [change.shopId] : change.shopIds;
/** The ids of the units a change creates. */
export const childrenOf = (change: PulseLayoutChange) => change.kind === 'split' ? change.shares.map((_, i) => derivedId(change, i)) : [derivedId(change)];

/** Why a change cannot apply to this venue, or null. `others` are the scenario's other changes. */
export function layoutError(venue: PropertyVenue, change: PulseLayoutChange, others: PulseLayoutChange[] = []): string | null {
  const units = new Map(venue.features.filter(f => f.kind === 'unit').map(f => [f.id, f]));
  const taken = new Set(others.flatMap(parentsOf));
  const parents = parentsOf(change);
  for (const id of parents) {
    if (!units.has(id)) return `No shop "${id}" on this centre.`;
    if (taken.has(id)) return `${id} is already reshaped in this scenario; restore it first.`;
  }
  if (!Number.isFinite(change.works) || change.works < 0) return 'Works must be zero or a positive amount in €.';
  if (change.kind === 'split') {
    const { shares } = change;
    if (shares.length < SPLIT_PARTS.min || shares.length > SPLIT_PARTS.max) return `A unit splits into ${SPLIT_PARTS.min} to ${SPLIT_PARTS.max} parts.`;
    if (shares.some(s => !Number.isFinite(s) || s < SHARE_MIN) || Math.abs(shares.reduce((a, b) => a + b, 0) - 1) > 0.01) return `Each part must hold at least ${SHARE_MIN * 100}% of the unit, and the parts must add up to the whole.`;
    return null;
  }
  if (parents.length < 2 || parents.length > 4 || new Set(parents).size !== parents.length) return 'A merge joins 2 to 4 different units.';
  const features = parents.map(id => units.get(id)!);
  if (new Set(features.map(f => f.level)).size > 1) return 'Units on different levels cannot be merged.';
  const p = projector(features[0]!.rings[0]![0]!);
  const rings = features.map(f => f.rings.map(r => r.map(pt => p.to(pt))));
  // Connected: every unit reaches the first through units that touch.
  const joined = new Set([0]);
  for (let grew = true; grew;) {
    grew = false;
    rings.forEach((r, i) => { if (!joined.has(i) && [...joined].some(j => ringsDistance(r, rings[j]!) <= ADJACENT_M)) { joined.add(i); grew = true; } });
  }
  return joined.size === parents.length ? null : `${parents.join(', ')} do not share walls; only neighbouring units can be merged.`;
}

/** The venue as a scenario reshapes it: parents out, the new units in, each remembering where it came from. Invalid changes are skipped. */
export function applyLayout(venue: PropertyVenue, changes: PulseLayoutChange[] | undefined): PropertyVenue {
  if (!changes?.length) return venue;
  const valid: PulseLayoutChange[] = [];
  for (const c of changes) if (!layoutError(venue, c, valid)) valid.push(c);
  if (!valid.length) return venue;
  const units = new Map(venue.features.map(f => [f.id, f]));
  const removed = new Set(valid.flatMap(parentsOf));
  const added: PulseVenueFeature[] = valid.flatMap(change => {
    if (change.kind === 'split') {
      const parent = units.get(change.shopId)!;
      const ring = largest(parent.rings);
      const p = projector(ring[0]!);
      return splitRing(ring.map(p.to), change.shares).map((part, i) => ({
        id: derivedId(change, i), kind: 'unit' as const, level: parent.level, name: null, rings: [part.map(p.from)],
        areaSqm: Math.round(parent.areaSqm * change.shares[i]!), exposure: parent.exposure ?? null,
        code: parent.code ? `${parent.code}-${i + 1}` : null, parentIds: [parent.id],
      }));
    }
    const parents = change.shopIds.map(id => units.get(id)!);
    const best = parents.reduce((a, b) => (b.exposure?.percentile ?? -1) > (a.exposure?.percentile ?? -1) ? b : a);
    return [{
      id: derivedId(change), kind: 'unit' as const, level: parents[0]!.level, name: null, rings: parents.flatMap(f => f.rings),
      areaSqm: parents.reduce((s, f) => s + f.areaSqm, 0), exposure: best.exposure ?? null,
      code: parents.map(f => f.code || f.id).join('+'), parentIds: change.shopIds,
    }];
  });
  return { ...venue, features: [...venue.features.filter(f => !removed.has(f.id)), ...added] };
}

/** Reads a stored layout back, keeping only well-formed changes. */
export function validLayout(value: unknown): value is PulseLayoutChange[] {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > LAYOUT_MAX) return false;
  return value.every(c => {
    if (!c || typeof c !== 'object') return false;
    const o = c as Record<string, unknown>;
    if (typeof o.works !== 'number' || !Number.isFinite(o.works) || o.works < 0) return false;
    if (o.kind === 'split') return typeof o.shopId === 'string' && o.shopId.length <= 100 && Array.isArray(o.shares) && o.shares.length <= SPLIT_PARTS.max && o.shares.every(s => typeof s === 'number' && Number.isFinite(s));
    if (o.kind === 'merge') return Array.isArray(o.shopIds) && o.shopIds.length <= 4 && o.shopIds.every(s => typeof s === 'string' && s.length <= 100);
    return false;
  });
}
